import React, { useEffect } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { motion } from 'framer-motion';
import { Dumbbell, Brain, Shield, Users, Heart, Activity, Smile, TrendingUp, Target, Wind, Moon, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
export function AdultBenefitsPage() {
  useEffect(() => {
    document.title = "Benefits for Adults | Walker's Martial Arts";
  }, []);
  const benefits = [{
    icon: Dumbbell,
    title: 'Physical Fitness',
    items: [{
      subtitle: 'Cardiovascular Health',
      description: 'Martial arts offer a great cardiovascular workout that helps improve heart health and stamina. Activities like sparring, shadowboxing, and forms can raise the heart rate and build endurance.'
    }, {
      subtitle: 'Strength and Flexibility',
      description: 'Regular practice of martial arts can build muscle strength and improve flexibility, especially with techniques that require balance, agility, and power.'
    }, {
      subtitle: 'Weight Management',
      description: 'As a consistent form of exercise, training can contribute to weight loss or the burning of calories and maintaining a healthy weight.'
    }, {
      subtitle: 'Coordination and Balance',
      description: 'Martial arts teach body awareness, which improves balance and coordination. This can be especially beneficial as adults age and want to reduce the risk of falls or decline.'
    }]
  }, {
    icon: Brain,
    title: 'Mental Benefits',
    items: [{
      subtitle: 'Focus and Discipline',
      description: 'Martial arts training requires concentration and focus on technique, footwork, or strategies. This can lead to improved mental discipline. The mental skills can translate over to other areas of life, such as work or personal relationships.'
    }, {
      subtitle: 'Stress Relief',
      description: 'The physical activity and focus involved can reduce stress or cortisol levels and stress hormones and help create a sense of calm and release tension.'
    }, {
      subtitle: 'Confidence and Self-Esteem',
      description: "As you progress in learning, training, and gain a sense of accomplishment, martial arts can boost confidence, improve self-esteem, or simply increase physical capabilities and self-efficacy (belief in one's ability to succeed)."
    }]
  }, {
    icon: Shield,
    title: 'Self-Defense Skills',
    items: [{
      subtitle: 'Practical Skills',
      description: "Learning martial arts provides practical self-defense techniques that can help you feel more secure and confident in potentially dangerous situations. Practicing can build confidence in one's ability to defend themselves in real-world situations."
    }, {
      subtitle: 'Situational Awareness',
      description: 'Many martial arts teach how to increase your situational awareness in various situations, which can help you avoid potentially dangerous situations or identify potential danger early.'
    }]
  }, {
    icon: Users,
    title: 'Social Benefits',
    items: [{
      subtitle: 'Sense of Community',
      description: 'Many martial arts schools encourage a sense of community with their peers, making adults have more social connections. Practicing alongside others helps create a sense of belonging.'
    }, {
      subtitle: 'Cultural Exposure',
      description: 'Martial arts often carry rich histories and philosophies, such as respect, humility, and perseverance. This provides opportunities for personal growth and developing a broader worldview.'
    }]
  }, {
    icon: Heart,
    title: 'Emotional Resilience',
    items: [{
      subtitle: 'Mental Toughness',
      description: "Martial arts training often involves overcoming personal challenges, whether it's pushing through tough classes or facing fears (like sparring). This builds resilience, helping practitioners deal with stress or adversity in other areas of life."
    }, {
      subtitle: 'Patience and Perseverance',
      description: 'The journey in martial arts can be long, requiring practice. Adults often face challenges as they work toward long-term goals. This can teach focus, perseverance, and long-term commitment.'
    }]
  }, {
    icon: Activity,
    title: 'Better Posture and Mobility',
    items: [{
      subtitle: 'Correcting Posture',
      description: 'Martial arts training can help improve posture by building core strength and body alignment by focusing on proper techniques and movement mechanics.'
    }, {
      subtitle: 'Injury Prevention',
      description: 'By building awareness, increased fitness and strength with core strength, martial arts can help prevent injuries in everyday life. Martial arts also emphasize proper stretching and movement, which can prevent injuries.'
    }]
  }, {
    icon: Wind,
    title: 'Improved Energy and Vitality',
    items: [{
      subtitle: 'Mental and Physical Focus',
      description: "Martial arts training can help you focus deeply and enhance a sense of calm. The techniques help clear and sharpen a person's mind, while providing stress relief, focus, and discipline."
    }]
  }, {
    icon: Smile,
    title: 'Improved Mental Health',
    items: [{
      subtitle: 'Reduction in Anxiety and Depression',
      description: 'The combination of physical activity, focus, engagement, and the mental discipline involved in martial arts can help reduce symptoms of anxiety and depression. The endorphins released during physical activity can improve mood and overall mental well-being.'
    }, {
      subtitle: 'Mindfulness and Meditation',
      description: 'Some styles offer slow, purposeful, meditative movement. Focusing on breathing and movement can serve as a powerful way to practice mindfulness.'
    }]
  }, {
    icon: Zap,
    title: 'A Fun Way to Stay Fit',
    items: [{
      subtitle: 'Enjoyable Exercise',
      description: "Martial arts are enjoyable for people of all ages. Training can be practical and engaging, whether it's through sparring, forms, or technique practice. Martial arts provide a great way to stay in better physical shape while having fun."
    }]
  }, {
    icon: TrendingUp,
    title: 'Goal Setting',
    items: [{
      subtitle: 'Achievement Milestones',
      description: 'Martial arts provide a way to set and achieve goals, offering a clear progression path that encourages motivation. This helps you achieve defined goals and milestones as you advance through the ranks.'
    }]
  }, {
    icon: Target,
    title: 'Improved Breathing and Vitality',
    items: [{
      subtitle: 'Breathing Techniques',
      description: 'Martial arts often teach breathing exercises that improve oxygen intake and lung capacity. Controlled deep breathing techniques provide a sense of calm, improve performance, and build endurance.'
    }]
  }, {
    icon: Moon,
    title: 'Better Sleep',
    items: [{
      subtitle: 'Physical Exercise Benefits',
      description: 'Physical exercise from intense martial arts training improves sleep quality. A regular practice can lead to better overall and more restorative sleep.'
    }]
  }];
  return <div className="bg-black min-h-screen" data-id="element-328">
      <Navigation data-id="element-329" />

      {/* Hero Section with Large Image */}
      <div className="relative pt-20 pb-16 overflow-hidden" data-id="element-330">
        <div className="absolute inset-0 z-0 bg-black" data-id="element-331" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16" data-id="element-332">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-center mb-12" data-id="element-333">
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6" data-id="element-334">
              Benefits for <span className="text-[#00D4FF]" data-id="element-335">Adults</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed" data-id="element-336">
              Walker's Martial Arts offers a wide range of benefits for adults,
              both physically and mentally. These disciplines are not just about
              fighting skills—they also promote overall well-being and personal
              growth. Here's a breakdown of the key benefits:
            </p>
          </motion.div>
        </div>
      </div>

      {/* Benefits Sections */}
      <section className="py-20 bg-[#0a0a0a]" data-id="element-337">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-338">
          <div className="space-y-20" data-id="element-339">
            {benefits.map((benefit, index) => <motion.div key={benefit.title} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6,
            delay: index * 0.05
          }} className="relative" data-id="element-340">
                {/* Section Header */}
                <div className="flex items-center gap-4 mb-8" data-id="element-341">
                  <div className="flex items-center justify-center w-14 h-14 bg-[#00D4FF]/10 rounded-2xl border border-[#00D4FF]/30" data-id="element-342">
                    <benefit.icon className="w-7 h-7 text-[#00D4FF]" data-id="element-343" />
                  </div>
                  <div data-id="element-344">
                    <span className="text-[#00D4FF] font-bold text-lg" data-id="element-345">
                      {index + 1}.
                    </span>
                    <h2 className="text-3xl font-bold text-white inline ml-3" data-id="element-346">
                      {benefit.title}
                    </h2>
                  </div>
                </div>

                {/* Items */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 ml-0 md:ml-18" data-id="element-347">
                  {benefit.items.map((item, itemIndex) => <motion.div key={item.subtitle} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: itemIndex * 0.1
              }} className="bg-[#1a1a1a] p-6 rounded-xl border border-gray-800" data-id="element-348">
                      <h3 className="text-lg font-bold text-white mb-3 flex items-start gap-2" data-id="element-349">
                        <div className="w-1.5 h-1.5 bg-[#00D4FF] rounded-full mt-2 flex-shrink-0" data-id="element-350" />
                        {item.subtitle}
                      </h3>
                      <p className="text-gray-400 leading-relaxed text-sm" data-id="element-351">
                        {item.description}
                      </p>
                    </motion.div>)}
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black" data-id="element-352">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-353">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center" data-id="element-354">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#00D4FF]/10 rounded-full mb-6" data-id="element-355">
              <Heart className="w-8 h-8 text-[#00D4FF]" data-id="element-356" />
            </div>
            <p className="text-2xl text-gray-300 leading-relaxed mb-12" data-id="element-357">
              Overall, martial arts offer a holistic approach to health and
              wellness for adults, combining physical fitness, mental clarity,
              and personal development in a supportive and engaging environment.
            </p>

            <div className="bg-gradient-to-r from-[#00D4FF]/10 to-transparent p-8 rounded-2xl border border-[#00D4FF]/20 mb-12" data-id="element-358">
              <p className="text-xl text-white font-semibold mb-6" data-id="element-359">
                Ready to experience these benefits for yourself?
              </p>
              <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-[#00D4FF] text-black font-bold rounded-full hover:bg-[#00D4FF]/90 transition-colors" data-id="element-360">
                <Activity className="w-5 h-5" data-id="element-361" />
                Start Your Journey Today
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-362" />
    </div>;
}